import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppContext } from '../../../contexts/AppContext';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useEstabsContext } from '../../../contexts/EstabsContext';
import { SALONFLOW_PLAN_LABEL, normalizePlanKey } from '../../../plans/salonflowPlans';
import type { SalonFlowPlanKey } from '../../../plans/salonflowPlans';
import { BillingSettingsService } from './Service';
import { Check, ClipboardList, CreditCard, Layers, Receipt, RefreshCw, Settings, Trash2, TriangleAlert, UserRoundCheck } from 'lucide-react';

const service = new BillingSettingsService();

type BillingCycle = 'monthly' | 'yearly';

const formatCurrency = (value?: number | null) =>
    Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (value?: string | Date | null) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('pt-BR');
};

const statusLabel = (status?: string | null) => {
    if (status === 'active') return 'Ativa';
    if (status === 'pending') return 'Pendente';
    if (status === 'past_due') return 'Em atraso';
    if (status === 'blocked') return 'Bloqueada';
    if (status === 'cancelled') return 'Cancelada';
    return 'Sem assinatura';
};

const statusBadge = (status?: string | null) => {
    if (status === 'active') return 'badge-success';
    if (status === 'pending') return 'badge-warning';
    if (status === 'past_due' || status === 'blocked') return 'badge-error';
    return 'badge-ghost';
};

const paymentMethodStatusLabel = (status?: string | null) => {
    if (status === 'active') return 'Ativa';
    if (status === 'inactive') return 'Inativa';
    if (status === 'deleted') return 'Removida';
    return 'Indefinida';
};

const paymentMethodStatusBadge = (status?: string | null) => {
    if (status === 'active') return 'badge-success';
    if (status === 'inactive') return 'badge-warning';
    if (status === 'deleted') return 'badge-ghost';
    return 'badge-ghost';
};

const cycleLabel = (cycle?: string | null) => {
    if (cycle === 'yearly') return 'Anual';
    if (cycle === 'monthly') return 'Mensal';
    return '-';
};

const billingTypeLabel = (billingType?: string | null) => {
    if (billingType === 'CREDIT_CARD') return 'Cartão recorrente';
    if (billingType === 'BOLETO') return 'Boleto';
    if (billingType === 'UNDEFINED') return 'Não informado (legado)';
    return '-';
};

const cardBrandLabel = (brand?: string | null) => {
    const normalized = String(brand || '').trim();
    return normalized ? normalized.toUpperCase() : '';
};

const paymentMethodSummary = (paymentMethod?: any) => {
    if (!paymentMethod) {
        return 'Nenhuma';
    }

    const brand = cardBrandLabel(paymentMethod.cardBrand);
    const last4 = String(paymentMethod.cardLast4 || '').trim();

    if (brand && last4) {
        return `${brand} final ${last4}`;
    }

    if (last4) {
        return `Cartão final ${last4}`;
    }

    if (brand) {
        return `Cartão ${brand}`;
    }

    return 'Cartão sem resumo';
};

const hasPaymentMethodCardSummary = (paymentMethod?: any) =>
    Boolean(String(paymentMethod?.cardBrand || '').trim() || String(paymentMethod?.cardLast4 || '').trim());

const normalizeCardBrand = (brand?: string | null) =>
    String(brand || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z]/g, '');

const PaymentBrandMark = ({
    brand,
    compact = false,
}: {
    brand?: string | null;
    compact?: boolean;
}) => {
    const normalized = normalizeCardBrand(brand);
    const brandAsset =
        normalized === 'visa'
            ? '/payment-brands/visa.svg'
            : normalized === 'mastercard' || normalized === 'master'
              ? '/payment-brands/mastercard.svg'
              : normalized === 'amex' || normalized === 'americanexpress'
                ? '/payment-brands/amex.svg'
                : normalized === 'elo'
                  ? '/payment-brands/elo.svg'
                  : null;

    if (brandAsset) {
        return (
            <img
                src={brandAsset}
                alt=""
                className={`${compact ? 'h-6 w-[58px]' : 'h-8 w-[72px]'} object-contain`}
            />
        );
    }

    return <CreditCard className="text-base-content/65" aria-hidden="true" />;
};

const PLAN_ORDER: SalonFlowPlanKey[] = ['trial', 'basic', 'professional', 'premium'];
const MODULE_LABELS: Record<string, string> = {
    agenda: 'Agenda',
    services: 'Serviços',
    attendants: 'Atendentes',
    clients: 'Clientes',
    'dashboard.basic': 'Dashboard básico',
    'dashboard.executive': 'Dashboard executivo',
    'dashboard.sales': 'Dashboard de vendas',
    'dashboard.stock': 'Dashboard de estoque',
    'dashboard.finance': 'Dashboard financeiro',
    public_page: 'Página pública',
    'landing_page.basic': 'Landing Page básica',
    'landing_page.full': 'Landing Page completa',
    plans: 'Planos de clientes',
    subscriptions: 'Assinaturas',
    subscription_periods: 'Períodos de assinatura',
    recurrences: 'Recorrências',
    sales: 'Vendas',
    waiting_list: 'Fila de espera',
    time_offers: 'Ofertas de horário',
    client_groups: 'Grupos de clientes',
    'whatsapp.operational': 'WhatsApp operacional',
    google_calendar: 'Google Calendar',
    stock: 'Estoque',
    finance: 'Financeiro',
    instagram: 'Instagram',
    notifications: 'Notificações internas',
};

const PREMIUM_MODULE_KEYS = [
    'agenda',
    'services',
    'attendants',
    'clients',
    'dashboard.basic',
    'dashboard.executive',
    'dashboard.sales',
    'dashboard.stock',
    'dashboard.finance',
    'public_page',
    'landing_page.basic',
    'landing_page.full',
    'plans',
    'subscriptions',
    'subscription_periods',
    'recurrences',
    'sales',
    'waiting_list',
    'time_offers',
    'client_groups',
    'whatsapp.operational',
    'google_calendar',
    'finance',
    'stock',
    'instagram',
    'notifications',
];

const MODULE_GROUPS = [
    {
        title: 'Operação',
        keys: ['agenda', 'services', 'attendants', 'clients', 'notifications'],
    },
    {
        title: 'Página pública',
        keys: ['public_page', 'landing_page.basic', 'landing_page.full'],
    },
    {
        title: 'Gestão e vendas',
        keys: [
            'plans',
            'subscriptions',
            'subscription_periods',
            'recurrences',
            'sales',
            'waiting_list',
            'time_offers',
            'client_groups',
        ],
    },
    {
        title: 'Canais e integrações',
        keys: ['whatsapp.operational', 'google_calendar', 'instagram'],
    },
    {
        title: 'Financeiro e estoque',
        keys: ['finance', 'stock'],
    },
    {
        title: 'Dashboards',
        keys: [
            'dashboard.basic',
            'dashboard.executive',
            'dashboard.sales',
            'dashboard.stock',
            'dashboard.finance',
        ],
    },
];

const getPlanModuleGroups = (modules?: string[]) => {
    if (!modules || modules.length === 0) {
        return [];
    }

    const moduleSet = new Set(modules[0] === '*' ? PREMIUM_MODULE_KEYS : modules);

    return MODULE_GROUPS.map((group) => ({
        title: group.title,
        labels: group.keys
            .filter((moduleKey) => moduleSet.has(moduleKey))
            .map((moduleKey) => MODULE_LABELS[moduleKey] || moduleKey),
    })).filter((group) => group.labels.length > 0);
};

const onlyDigits = (value?: string | null) => String(value || '').replace(/\D/g, '');

const clearDigits = (value?: string | null) => String(value || '').replace(/[^\d]/g, '');

const applyPattern = (digits: string, pattern: string) => {
    let index = 0;

    return pattern
        .split('')
        .map((char) => {
            if (char !== '#') {
                return char;
            }

            const digit = digits[index];
            index += 1;
            return digit || '';
        })
        .join('')
        .replace(/[^\d]+$/g, '');
};

const formatCpfCnpj = (value?: string | null) => {
    const digits = onlyDigits(value).slice(0, 14);

    if (digits.length <= 11) {
        return applyPattern(digits, '###.###.###-##');
    }

    return applyPattern(digits, '##.###.###/####-##');
};

const normalizeBrazilPhoneDigits = (value?: string | null) => {
    const digits = onlyDigits(value).slice(0, 13);

    if (digits.startsWith('55') && digits.length > 11) {
        return digits.slice(2, 13);
    }

    return digits.slice(0, 11);
};

const formatBrazilPhone = (value?: string | null) => {
    const digits = normalizeBrazilPhoneDigits(value);

    if (digits.length <= 10) {
        return applyPattern(digits, '(##) ####-####');
    }

    return applyPattern(digits, '(##) #####-####');
};

const hasValidBrazilPhone = (value?: string | null) => {
    const digits = normalizeBrazilPhoneDigits(value);
    return digits.length === 10 || digits.length === 11;
};

const formatCep = (value?: string | null) => applyPattern(onlyDigits(value).slice(0, 8), '#####-###');

const formatCardNumber = (value?: string | null) => {
    const digits = onlyDigits(value).slice(0, 19);
    return digits.replace(/(.{4})/g, '$1 ').trim();
};

const formatCardExpiryMonth = (value?: string | null) => onlyDigits(value).slice(0, 2);

const formatCardExpiryYear = (value?: string | null) => onlyDigits(value).slice(0, 4);

const formatSecurityCode = (value?: string | null) => onlyDigits(value).slice(0, 4);

const formatAddressNumber = (value?: string | null) => onlyDigits(value).slice(0, 8);

const requiredLabel = (label: string) => (
    <span className="label-text flex items-center gap-1">
        <span>{label}</span>
        <span className="text-sm font-semibold leading-none text-error" aria-hidden="true">
            *
        </span>
    </span>
);

const inputClassName = (invalid: boolean) =>
    `input input-bordered ${invalid ? 'input-error border-error focus:border-error' : ''}`;

const requiredFieldHint = (invalid: boolean) =>
    invalid ? <span className="mt-1 text-xs font-medium text-error">Campo obrigatório.</span> : null;

const checkoutErrorMessage = (error: any) => {
    const data = error?.response?.data;

    if (data?.code === 'ASAAS_NOT_CONFIGURED') {
        return 'A integração Asaas ainda não foi configurada. Configure ASAAS_API_KEY antes de contratar.';
    }

    if (data?.code === 'BILLING_CUSTOMER_DATA_REQUIRED') {
        return data.message || 'Preencha os dados de cobrança obrigatórios.';
    }

    if (data?.code === 'BILLING_CREDIT_CARD_REQUIRED') {
        return data.message || 'Preencha os dados do cartão para concluir a contratação.';
    }

    if (data?.code === 'BILLING_CREDIT_CARD_HOLDER_REQUIRED') {
        return data.message || 'Preencha os dados do titular do cartão.';
    }

    if (data?.code === 'BILLING_REMOTE_IP_REQUIRED') {
        return data.message || 'Não foi possível identificar a origem da contratação.';
    }

    if (data?.code === 'BILLING_PAYMENT_METHOD_REQUIRED') {
        return data.message || 'Adicione uma forma de pagamento antes de contratar.';
    }

    if (data?.code === 'BILLING_TERMS_REQUIRED') {
        return data.message || 'Aceite os termos para continuar.';
    }

    if (data?.code === 'BILLING_DOWNGRADE_NOT_SUPPORTED') {
        return data.message || 'Para contratar um plano menor, cancele a assinatura atual e contrate outro plano.';
    }

    if (data?.code === 'BILLING_PLAN_PRICE_MISMATCH') {
        return data.message || 'O preço do plano mudou. Atualize a tela e tente novamente.';
    }

    if (data?.code === 'BILLING_EXEMPT') {
        return data.message || 'Este estabelecimento possui contrato externo e não precisa contratar plano.';
    }

    if (data?.code === 'ASAAS_REQUEST_FAILED') {
        return data.message || 'O Asaas recusou a solicitação. Confira os dados de cobrança e tente novamente.';
    }

    return data?.message || 'Não foi possível iniciar a contratação.';
};

export default function BillingSettings() {
    const { user } = useAuthContext();
    const { estabSession, reloadEstab } = useEstabsContext();
    const { successSnackbar, errorSnackbar, triggerPlanCelebration } = useAppContext();
    const errorSnackbarRef = useRef(errorSnackbar);
    const [searchParams, setSearchParams] = useSearchParams();
    const [plans, setPlans] = useState<any[]>([]);
    const [subscriptionData, setSubscriptionData] = useState<any>(null);
    const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
    const [cycle, setCycle] = useState<BillingCycle>('monthly');
    const [activeSection, setActiveSection] = useState<'resumo' | 'planos' | 'pagamentos' | 'cobrancas'>('resumo');
    const [loading, setLoading] = useState(true);
    const [submittingPlan, setSubmittingPlan] = useState<string | null>(null);
    const [cancellingSubscription, setCancellingSubscription] = useState(false);
    const [savingPaymentMethod, setSavingPaymentMethod] = useState(false);
    const [paymentMethodDialogOpen, setPaymentMethodDialogOpen] = useState(false);
    const [paymentMethodSubmitAttempted, setPaymentMethodSubmitAttempted] = useState(false);
    const [checkoutPlan, setCheckoutPlan] = useState<any>(null);
    const [cancelRenewalDialogOpen, setCancelRenewalDialogOpen] = useState(false);
    const [checkoutTermsAccepted, setCheckoutTermsAccepted] = useState(false);
    const [returnNoticeDismissed, setReturnNoticeDismissed] = useState(false);
    const [checkoutStarted, setCheckoutStarted] = useState(
        () =>
            typeof window !== 'undefined' &&
            window.sessionStorage.getItem('salonflowBillingCheckoutStarted') === 'true',
    );
    const planActivationCelebrationKeyRef = useRef<string | null>(null);
    const [customer, setCustomer] = useState({
        name: '',
        cpfCnpj: '',
        email: '',
        mobilePhone: '',
    });
    const [holderInfo, setHolderInfo] = useState({
        name: '',
        email: '',
        cpfCnpj: '',
        postalCode: '',
        addressNumber: '',
    });
    const [creditCard, setCreditCard] = useState({
        number: '',
        expiryMonth: '',
        expiryYear: '',
        ccv: '',
    });

    const billingExempt = Boolean((estabSession as any)?.billingExempt || subscriptionData?.billingExempt);
    const currentPlanKey = billingExempt ? 'premium' : normalizePlanKey((estabSession as any)?.systemPlanKey);
    const currentSubscription = subscriptionData?.subscription;
    const payments = subscriptionData?.payments || [];
    const gateway = subscriptionData?.gateway || {};
    const defaultPaymentMethod =
        paymentMethods.find((paymentMethod: any) => paymentMethod.isDefault) || paymentMethods[0] || null;
    const selectedPaymentMethod = defaultPaymentMethod;
    const latestPayment = payments[0];
    const hasPendingPayment = currentSubscription?.status === 'pending' || latestPayment?.status === 'pending';
    const accessEndsAt =
        currentSubscription?.accessEndsAt ||
        currentSubscription?.currentPeriodEndAt ||
        currentSubscription?.nextDueDate;
    const cancellationScheduled = Boolean(
        currentSubscription?.status === 'cancelled' &&
        currentSubscription?.cancelAtPeriodEnd &&
        accessEndsAt &&
        new Date(accessEndsAt).getTime() > Date.now(),
    );
    const canCancelSubscription =
        currentSubscription?.status === 'active' && !currentSubscription?.cancelAtPeriodEnd;

    const billablePlans = useMemo(
        () => plans.filter((plan) => plan.key !== 'trial' && !!plan.billing),
        [plans],
    );
    const currentPlan = plans.find((plan) => plan.key === currentPlanKey) || null;
    const currentPlanPrice =
        currentSubscription?.amount ??
        currentPlan?.billing?.[currentSubscription?.cycle || cycle]?.amount ??
        null;
    const checkoutPlanPrice = checkoutPlan?.billing?.[cycle]?.amount;
    const checkoutPlanOrder = checkoutPlan ? PLAN_ORDER.indexOf(checkoutPlan.key) : -1;
    const currentPlanOrder = PLAN_ORDER.indexOf(currentPlanKey);
    const checkoutIsSmallerPlan =
        !!checkoutPlan &&
        currentSubscription?.status === 'active' &&
        checkoutPlanOrder >= 0 &&
        checkoutPlanOrder < currentPlanOrder;
    const paymentMethodFieldErrors = {
        name: paymentMethodSubmitAttempted && !customer.name.trim(),
        cpfCnpj: paymentMethodSubmitAttempted && !onlyDigits(customer.cpfCnpj),
        email: paymentMethodSubmitAttempted && !customer.email.trim(),
        mobilePhone: paymentMethodSubmitAttempted && !hasValidBrazilPhone(customer.mobilePhone),
        postalCode: paymentMethodSubmitAttempted && !onlyDigits(holderInfo.postalCode),
        addressNumber: paymentMethodSubmitAttempted && !holderInfo.addressNumber.trim(),
        cardNumber: paymentMethodSubmitAttempted && !onlyDigits(creditCard.number),
        expiryMonth: paymentMethodSubmitAttempted && !onlyDigits(creditCard.expiryMonth),
        expiryYear: paymentMethodSubmitAttempted && !onlyDigits(creditCard.expiryYear),
        ccv: paymentMethodSubmitAttempted && !onlyDigits(creditCard.ccv),
    };

    const sectionTabs = [
        { id: 'resumo', label: 'Resumo', icon: ClipboardList },
        { id: 'planos', label: 'Planos', icon: Layers },
        { id: 'pagamentos', label: 'Pagamentos', icon: CreditCard },
        { id: 'cobrancas', label: 'Cobranças', icon: Receipt },
    ] as const;

    useEffect(() => {
        errorSnackbarRef.current = errorSnackbar;
    }, [errorSnackbar]);

    const returnNotice = useMemo(() => {
        const status = searchParams.get('billing') || searchParams.get('status');
        const subscriptionSettled =
            currentSubscription?.status === 'active' ||
            (currentSubscription?.status === 'cancelled' && cancellationScheduled);

        if (subscriptionSettled) {
            return null;
        }

        if (status === 'success' || status === 'paid' || status === 'confirmed') {
            return {
                className: 'alert-success',
                message: 'Pagamento recebido. A liberação do plano acontece assim que o webhook do Asaas confirmar a cobrança.',
            };
        }

        if ((status === 'pending' || checkoutStarted || hasPendingPayment) && !returnNoticeDismissed) {
            return {
                className: 'alert-info',
                message: 'Pagamento iniciado no Asaas. Se você já pagou, aguarde alguns instantes e atualize esta tela.',
            };
        }

        if (status === 'failed' || status === 'cancelled') {
            return {
                className: 'alert-warning',
                message: 'Pagamento não confirmado. Confira a cobrança no Asaas ou tente novamente.',
            };
        }

        return null;
    }, [
        cancellationScheduled,
        checkoutStarted,
        currentSubscription?.status,
        hasPendingPayment,
        returnNoticeDismissed,
        searchParams,
    ]);

    const clearReturnNotice = () => {
        if (typeof window !== 'undefined') {
            window.sessionStorage.removeItem('salonflowBillingCheckoutStarted');
        }

        setCheckoutStarted(false);
        setReturnNoticeDismissed(true);

        const next = new URLSearchParams(searchParams);
        next.delete('billing');
        next.delete('status');
        next.delete('paymentId');
        setSearchParams(next, { replace: true });
    };

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [plansResponse, subscriptionResponse, paymentMethodsResponse] = await Promise.all([
                service.getPlans(),
                service.getSubscription(),
                service.getPaymentMethods(),
            ]);
            setPlans(plansResponse.data || []);
            setSubscriptionData(subscriptionResponse.data || null);
            setPaymentMethods(paymentMethodsResponse.data?.paymentMethods || []);
        } catch (error: any) {
            console.error('billing-load', error?.response?.data?.code || error?.message || 'unknown');
            errorSnackbarRef.current?.(
                error?.response?.data?.message || 'Não foi possível carregar os dados de cobrança.',
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        setCustomer((prev) => ({
            name: prev.name || (estabSession as any)?.name || user?.displayName || '',
            cpfCnpj: prev.cpfCnpj,
            email: prev.email || user?.email || '',
            mobilePhone: prev.mobilePhone || user?.phoneNumber || (estabSession as any)?.phoneNumber || '',
        }));
    }, [estabSession, user?.displayName, user?.email, user?.phoneNumber]);

    useEffect(() => {
        setHolderInfo((prev) => ({
            name: prev.name || customer.name || (estabSession as any)?.name || user?.displayName || '',
            email: prev.email || customer.email || user?.email || '',
            cpfCnpj: prev.cpfCnpj || customer.cpfCnpj,
            postalCode: prev.postalCode,
            addressNumber: prev.addressNumber,
        }));
    }, [customer, estabSession, user?.displayName, user?.email]);

    useEffect(() => {
        if (currentSubscription?.status !== 'active' || !checkoutStarted) {
            return;
        }

        if (typeof window !== 'undefined') {
            window.sessionStorage.removeItem('salonflowBillingCheckoutStarted');
        }

        setCheckoutStarted(false);
    }, [checkoutStarted, currentSubscription?.status]);

    useEffect(() => {
        if (currentSubscription?.status !== 'active') {
            return;
        }

        const currentStatusParam = searchParams.get('billing') || searchParams.get('status');
        const shouldClearReturnState =
            checkoutStarted ||
            currentStatusParam === 'pending' ||
            currentStatusParam === 'success' ||
            currentStatusParam === 'paid' ||
            currentStatusParam === 'confirmed';

        if (!shouldClearReturnState) {
            return;
        }

        if (typeof window !== 'undefined') {
            window.sessionStorage.removeItem('salonflowBillingCheckoutStarted');
        }

        setCheckoutStarted(false);
        setReturnNoticeDismissed(true);

        const next = new URLSearchParams(searchParams);
        next.delete('billing');
        next.delete('status');
        next.delete('paymentId');
        setSearchParams(next, { replace: true });
    }, [checkoutStarted, currentSubscription?.status, searchParams, setSearchParams]);

    useEffect(() => {
        if (currentSubscription?.status !== 'cancelled' || !cancellationScheduled) {
            return;
        }

        const currentStatusParam = searchParams.get('billing') || searchParams.get('status');
        const shouldClearReturnState =
            checkoutStarted ||
            currentStatusParam === 'pending' ||
            currentStatusParam === 'success' ||
            currentStatusParam === 'paid' ||
            currentStatusParam === 'confirmed';

        if (!shouldClearReturnState) {
            return;
        }

        if (typeof window !== 'undefined') {
            window.sessionStorage.removeItem('salonflowBillingCheckoutStarted');
        }

        setCheckoutStarted(false);
        setReturnNoticeDismissed(true);

        const next = new URLSearchParams(searchParams);
        next.delete('billing');
        next.delete('status');
        next.delete('paymentId');
        setSearchParams(next, { replace: true });
    }, [cancellationScheduled, checkoutStarted, currentSubscription?.status, searchParams, setSearchParams]);

    useEffect(() => {
        const returnStatus = searchParams.get('billing') || searchParams.get('status');
        const cameFromSuccessfulActivation =
            currentSubscription?.status === 'active' &&
            (checkoutStarted || returnStatus === 'success' || returnStatus === 'paid' || returnStatus === 'confirmed');

        if (!cameFromSuccessfulActivation) {
            return;
        }

        const celebrationKey = [
            currentSubscription?._id || currentSubscription?.id || currentSubscription?.providerSubscriptionId || 'active',
            accessEndsAt || '',
            currentSubscription?.status || '',
        ].join(':');

        if (planActivationCelebrationKeyRef.current === celebrationKey) {
            return;
        }

        planActivationCelebrationKeyRef.current = celebrationKey;
        triggerPlanCelebration({
            title: 'Plano ativado',
            message: 'Tudo certo com a cobrança. Seu acesso já foi liberado.',
        });
    }, [accessEndsAt, checkoutStarted, currentSubscription, searchParams, triggerPlanCelebration]);

    const validateCustomer = () => {
        const missingFields: string[] = [];

        if (!customer.name.trim()) {
            missingFields.push('nome/razão social');
        }

        if (!onlyDigits(customer.cpfCnpj)) {
            missingFields.push('CPF/CNPJ');
        }

        if (!customer.email.trim()) {
            missingFields.push('e-mail');
        }

        if (!hasValidBrazilPhone(customer.mobilePhone)) {
            missingFields.push('telefone');
        }

        if (missingFields.length > 0) {
            errorSnackbar(`Preencha os dados de cobrança obrigatórios: ${missingFields.join(', ')}.`);
            return false;
        }

        return true;
    };

    const validateHolderInfo = () => {
        const missingFields: string[] = [];
        const holderName = String(customer.name || holderInfo.name || '').trim();
        const holderEmail = String(customer.email || holderInfo.email || '').trim();
        const holderCpfCnpj = onlyDigits(customer.cpfCnpj || holderInfo.cpfCnpj);

        if (!holderName) {
            missingFields.push('nome do titular');
        }

        if (!holderEmail) {
            missingFields.push('e-mail do titular');
        }

        if (!holderCpfCnpj) {
            missingFields.push('CPF/CNPJ do titular');
        }

        if (!onlyDigits(holderInfo.postalCode)) {
            missingFields.push('CEP');
        }

        if (!holderInfo.addressNumber.trim()) {
            missingFields.push('número do endereço');
        }

        if (!hasValidBrazilPhone(customer.mobilePhone)) {
            missingFields.push('telefone do titular');
        }

        if (missingFields.length > 0) {
            errorSnackbar(`Preencha os dados do titular do cartão: ${missingFields.join(', ')}.`);
            return false;
        }

        return true;
    };

    const validateCreditCard = () => {
        const missingFields: string[] = [];
        const holderName = String(customer.name).trim();

        if (!holderName) {
            missingFields.push('nome impresso');
        }

        if (!onlyDigits(creditCard.number)) {
            missingFields.push('número');
        }

        if (!onlyDigits(creditCard.expiryMonth)) {
            missingFields.push('mês de validade');
        }

        if (!onlyDigits(creditCard.expiryYear)) {
            missingFields.push('ano de validade');
        }

        if (!onlyDigits(creditCard.ccv)) {
            missingFields.push('CVV');
        }

        if (missingFields.length > 0) {
            errorSnackbar(`Preencha os dados do cartão: ${missingFields.join(', ')}.`);
            return false;
        }

        return true;
    };

    const resetSensitiveFields = () => {
        setCreditCard({
            number: '',
            expiryMonth: '',
            expiryYear: '',
            ccv: '',
        });
    };

    const closePaymentMethodDialog = () => {
        setPaymentMethodDialogOpen(false);
        setPaymentMethodSubmitAttempted(false);
        resetSensitiveFields();
    };

    const savePaymentMethod = async () => {
        setPaymentMethodSubmitAttempted(true);

        if (!validateCustomer() || !validateHolderInfo() || !validateCreditCard()) {
            return;
        }

        try {
            setSavingPaymentMethod(true);
            const holderName = String(customer.name || holderInfo.name || '').trim();
            const holderEmail = String(customer.email || holderInfo.email || '').trim();
            const holderCpfCnpj = onlyDigits(customer.cpfCnpj || holderInfo.cpfCnpj);
            const holderMobilePhone = normalizeBrazilPhoneDigits(customer.mobilePhone);
            await service.createPaymentMethod({
                customer,
                makeDefault: paymentMethods.length === 0,
                creditCard: {
                    holderName,
                    number: clearDigits(creditCard.number),
                    expiryMonth: clearDigits(creditCard.expiryMonth),
                    expiryYear: clearDigits(creditCard.expiryYear),
                    ccv: clearDigits(creditCard.ccv),
                },
                creditCardHolderInfo: {
                    name: holderName,
                    email: holderEmail,
                    cpfCnpj: holderCpfCnpj,
                    postalCode: onlyDigits(holderInfo.postalCode),
                    addressNumber: holderInfo.addressNumber.trim(),
                    addressComplement: null,
                    phone: null,
                    mobilePhone: holderMobilePhone,
                },
            });

            successSnackbar('Forma de pagamento adicionada.');
            closePaymentMethodDialog();
            await load();
        } catch (error: any) {
            console.error('billing-payment-method-create', error?.response?.data?.code || error?.message || 'unknown');
            errorSnackbar(
                error?.response?.data?.message || 'Não foi possível cadastrar a forma de pagamento.',
            );
        } finally {
            setSavingPaymentMethod(false);
        }
    };

    const startCheckout = async (plan: any) => {
        if (!selectedPaymentMethod?.id) {
            errorSnackbar('Adicione uma forma de pagamento antes de contratar o plano.');
            return;
        }

        if (!checkoutTermsAccepted) {
            errorSnackbar('Você precisa aceitar os termos para continuar.');
            return;
        }

        try {
            const planKey = plan.key as SalonFlowPlanKey;
            setSubmittingPlan(planKey);
            const response = await service.createCheckout({
                planKey,
                cycle,
                expectedAmount: plan.billing?.[cycle]?.amount,
                billingType: 'CREDIT_CARD',
                paymentMethodId: selectedPaymentMethod.id,
                acceptTerms: true,
            });

            await reloadEstab();
            resetSensitiveFields();
            triggerPlanCelebration({
                title:
                    response.data?.mode === 'subscription_reactivated'
                        ? 'Nova assinatura iniciada'
                        : response.data?.mode === 'subscription_updated'
                          ? 'Assinatura atualizada'
                          : 'Assinatura criada',
                message: 'A cobrança foi enviada ao Asaas. O plano será liberado assim que o pagamento for confirmado.',
            });
            successSnackbar(
                response.data?.mode === 'subscription_updated'
                    ? 'Assinatura atualizada. Aguarde a confirmação do pagamento.'
                    : response.data?.mode === 'subscription_reactivated'
                      ? 'Nova assinatura iniciada. Um novo ciclo foi aberto.'
                    : 'Assinatura criada. Aguarde a confirmação do pagamento.',
            );
            setCheckoutPlan(null);
            setCheckoutTermsAccepted(false);
            await load();
        } catch (error: any) {
            console.error('billing-checkout', error?.response?.data?.code || error?.message || 'unknown');
            errorSnackbar(checkoutErrorMessage(error));
        } finally {
            resetSensitiveFields();
            setSubmittingPlan(null);
        }
    };

    const openCheckoutConfirmation = (plan: any) => {
        setCheckoutPlan(plan);
        setCheckoutTermsAccepted(false);
    };

    const closeCheckoutConfirmation = () => {
        if (submittingPlan) {
            return;
        }

        setCheckoutPlan(null);
        setCheckoutTermsAccepted(false);
    };

    const setDefaultPaymentMethod = async (id: string) => {
        try {
            await service.setDefaultPaymentMethod(id);
            successSnackbar('Forma de pagamento definida como principal.');
            await load();
        } catch (error: any) {
            console.error('billing-payment-method-default', error?.response?.data?.code || error?.message || 'unknown');
            errorSnackbar(
                error?.response?.data?.message || 'Não foi possível definir a forma de pagamento como principal.',
            );
        }
    };

    const deletePaymentMethod = async (id: string) => {
        const confirmed = window.confirm('Desativar esta forma de pagamento?');
        if (!confirmed) {
            return;
        }

        try {
            await service.deletePaymentMethod(id);
            successSnackbar('Forma de pagamento desativada.');
            await load();
        } catch (error: any) {
            console.error('billing-payment-method-delete', error?.response?.data?.code || error?.message || 'unknown');
            if (error?.response?.data?.code === 'BILLING_PAYMENT_METHOD_LINKED_TO_SUBSCRIPTION') {
                errorSnackbar(
                    error.response.data.message ||
                        'Esta forma de pagamento está em uso. Cancele a assinatura antes de remover.',
                );
                return;
            }

            errorSnackbar(error?.response?.data?.message || 'Não foi possível desativar a forma de pagamento.');
        }
    };

    const cancelSubscription = async () => {
        try {
            setCancellingSubscription(true);
            const response = await service.cancelSubscription();
            const responseAccessEndsAt = response.data?.accessEndsAt || accessEndsAt;
            setCancelRenewalDialogOpen(false);
            successSnackbar(`Renovação cancelada. Acesso vigente até ${formatDate(responseAccessEndsAt)}.`);
            await load();
            await reloadEstab();
        } catch (error: any) {
            console.error('billing-cancel', error?.response?.data?.code || error?.message || 'unknown');
            errorSnackbar(error?.response?.data?.message || 'Não foi possível cancelar a assinatura.');
        } finally {
            setCancellingSubscription(false);
        }
    };

    if (loading) {
        return (
            <div className="card sf-dashboard-panel">
                <div className="card-body">Carregando cobrança...</div>
            </div>
        );
    }

    if (billingExempt) {
        return (
            <div className="flex flex-col gap-6 pb-6">
                <div className="card sf-dashboard-panel">
                    <div className="card-body gap-5">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div>
                                <h3 className="text-lg font-semibold">Plano do estabelecimento</h3>
                                <p className="text-sm text-base-content/60">
                                    {((estabSession as any)?.name || 'Estabelecimento')} possui contrato externo e acesso total.
                                </p>
                            </div>
                            <span className="badge badge-success w-fit">Contrato externo</span>
                        </div>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                            <div className="rounded-2xl border border-base-200 bg-base-100/70 p-4">
                                <div className="text-xs uppercase text-base-content/55">Cobrança</div>
                                <div className="mt-2 font-semibold">Controlada externamente</div>
                            </div>
                            <div className="rounded-2xl border border-base-200 bg-base-100/70 p-4">
                                <div className="text-xs uppercase text-base-content/55">Acesso</div>
                                <div className="mt-2 font-semibold">{SALONFLOW_PLAN_LABEL[currentPlanKey]}</div>
                            </div>
                            <div className="rounded-2xl border border-base-200 bg-base-100/70 p-4">
                                <div className="text-xs uppercase text-base-content/55">Limites</div>
                                <div className="mt-2 font-semibold">Sem limite por plano</div>
                            </div>
                        </div>

                        <div className="alert alert-info">
                            <div>
                                <div className="font-semibold">Contratação pelo SalonFlow desativada</div>
                                <div className="text-sm">
                                    Este estabelecimento não precisa assinar planos pelo Asaas enquanto a isenção estiver ativa.
                                </div>
                            </div>
                        </div>

                        <button type="button" className="btn btn-outline btn-sm w-fit" onClick={load}>
                            Atualizar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const renderSectionNav = () => (
        <div className="rounded-2xl border border-base-200 bg-base-100/90 shadow-sm">
            <div className="flex w-full flex-col gap-2 p-2 lg:flex-row lg:items-center">
                <div className="flex w-full gap-1">
                    {sectionTabs.map((tab) => {
                        const isActive = activeSection === tab.id;
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                className={`flex min-w-0 flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition ${
                                    isActive
                                        ? 'bg-primary/10 text-primary'
                                        : 'bg-base-200/60 text-base-content/80 hover:bg-base-200/80'
                                }`}
                                onClick={() => setActiveSection(tab.id)}
                            >
                                <tab.icon
                                    className={isActive ? 'text-primary' : 'text-base-content/70'}
                                    aria-hidden="true"
                                />
                                <span className="truncate">{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
                <div className="join self-stretch lg:ml-auto">
                    <button
                        type="button"
                        className={`btn btn-sm join-item ${cycle === 'monthly' ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => setCycle('monthly')}
                    >
                        Mensal
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm join-item ${cycle === 'yearly' ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => setCycle('yearly')}
                    >
                        Anual
                    </button>
                </div>
            </div>
        </div>
    );

    const renderSection = () => {
        if (activeSection === 'planos') {
            return (
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                    {billablePlans.map((plan) => {
                        const price = plan.billing?.[cycle]?.amount;
                        const isCurrent =
                            currentPlanKey === plan.key &&
                            currentSubscription?.status === 'active' &&
                            !currentSubscription?.cancelAtPeriodEnd;
                        const targetOrder = PLAN_ORDER.indexOf(plan.key);
                        const currentOrder = PLAN_ORDER.indexOf(currentPlanKey);
                        const isSmallerPlan = currentSubscription?.status === 'active' && targetOrder < currentOrder;
                        const needsRegularization =
                            currentSubscription?.status === 'pending' ||
                            currentSubscription?.status === 'past_due' ||
                            currentSubscription?.status === 'blocked';
                        const actionLabel = needsRegularization
                            ? 'Regularizar meu plano'
                            : isSmallerPlan
                              ? 'Cancelar para trocar'
                              : 'Contratar agora';
                        const moduleGroups = getPlanModuleGroups(plan.modules);

                        return (
                            <div key={plan.key} className="card sf-dashboard-panel">
                                <div className="card-body gap-5">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <h3 className="text-xl font-semibold">{plan.name}</h3>
                                            <p className="mt-1 text-sm text-base-content/60">
                                                {plan.key === 'basic' && 'Agenda, clientes, serviços e página pública.'}
                                                {plan.key === 'professional' &&
                                                    'Recorrências, vendas, assinaturas e operação avançada.'}
                                                {plan.key === 'premium' && 'Todos os módulos e dashboards completos.'}
                                            </p>
                                        </div>
                                        {isCurrent && <span className="badge badge-success">Atual</span>}
                                    </div>

                                    <div>
                                        <span className="text-3xl font-semibold">{formatCurrency(price)}</span>
                                        <span className="ml-1 text-sm text-base-content/60">
                                            /{cycle === 'yearly' ? 'ano' : 'mês'}
                                        </span>
                                    </div>

                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2 text-sm font-semibold text-base-content/80">
                                            <Check className="text-success" aria-hidden="true" />
                                            Módulos inclusos
                                        </div>
                                        <div className="max-h-64 space-y-3 overflow-y-auto rounded-2xl border border-base-200 bg-base-100/60 p-3">
                                            {moduleGroups.map((group) => (
                                                <div key={`${plan.key}-${group.title}`}>
                                                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-base-content/45">
                                                        {group.title}
                                                    </div>
                                                    <ul className="space-y-1">
                                                        {group.labels.map((moduleLabel) => (
                                                            <li
                                                                key={`${plan.key}-${group.title}-${moduleLabel}`}
                                                                className="flex items-start gap-2 text-xs text-base-content/75"
                                                            >
                                                                <Check className="mt-0.5 text-[10px] text-success" aria-hidden="true" />
                                                                <span>{moduleLabel}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <ul className="space-y-2 text-sm text-base-content/70">
                                        <li className="flex gap-2">
                                            <UserRoundCheck className="mt-1 text-success" aria-hidden="true" />
                                            {plan.limits?.attendants ? `${plan.limits.attendants} atendentes` : 'Atendentes ilimitados'}
                                        </li>
                                    </ul>

                                    <button
                                        type="button"
                                        className={`btn ${isCurrent ? 'btn-outline' : 'btn-primary'} mt-auto`}
                                        disabled={!!submittingPlan || isCurrent || !selectedPaymentMethod}
                                        onClick={() => {
                                            if (isSmallerPlan) {
                                                setActiveSection('resumo');
                                                errorSnackbar(
                                                    'Para contratar um plano menor, cancele a assinatura atual e depois contrate o novo plano.',
                                                );
                                                return;
                                            }

                                            openCheckoutConfirmation(plan);
                                        }}
                                    >
                                        {submittingPlan === plan.key && 'Preparando...'}
                                        {submittingPlan !== plan.key && isCurrent && 'Plano atual'}
                                        {submittingPlan !== plan.key && !isCurrent && !selectedPaymentMethod && 'Adicione uma forma de pagamento'}
                                        {submittingPlan !== plan.key && !isCurrent && selectedPaymentMethod && actionLabel}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }

        if (activeSection === 'pagamentos') {
            return (
                <div className="card sf-dashboard-panel">
                    <div className="card-body gap-5">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h3 className="text-lg font-semibold">Formas de pagamento</h3>
                                <p className="text-sm text-base-content/60">
                                    Cartões vinculados ao estabelecimento. A forma principal é usada nas contratações.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => setPaymentMethodDialogOpen(true)}
                            >
                                Adicionar forma
                            </button>
                        </div>

                        {paymentMethods.length === 0 ? (
                            <div className="alert alert-info">
                                <div>
                                    <div className="font-semibold">Nenhuma forma cadastrada</div>
                                    <div className="text-sm">
                                        Adicione um cartão para contratar planos sem depender de dados brutos de cartão.
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                                {paymentMethods.map((paymentMethod: any) => (
                                    <div
                                        key={paymentMethod.id}
                                        className={`rounded-xl border p-4 transition ${
                                            paymentMethod.isDefault
                                                ? 'border-primary bg-primary/5'
                                                : 'border-base-200 bg-base-100/70'
                                        }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="flex h-10 w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-lg border border-base-200 bg-white">
                                                <PaymentBrandMark brand={paymentMethod.cardBrand} />
                                            </div>

                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <div className="truncate font-semibold">
                                                        {paymentMethodSummary(paymentMethod)}
                                                    </div>
                                                    {paymentMethod.isDefault && (
                                                        <span className="badge badge-primary badge-sm">Principal</span>
                                                    )}
                                                </div>
                                                <div className="mt-1 text-xs text-base-content/60">
                                                    {hasPaymentMethodCardSummary(paymentMethod)
                                                        ? billingTypeLabel(paymentMethod.type)
                                                        : 'Resumo indisponível para este cadastro'}
                                                    {' · '}
                                                    Criada em {formatDate(paymentMethod.createdAt)}
                                                </div>
                                            </div>

                                            <span className={`badge ${paymentMethodStatusBadge(paymentMethod.status)}`}>
                                                {paymentMethodStatusLabel(paymentMethod.status)}
                                            </span>
                                        </div>

                                        <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-base-200 pt-3">
                                            {!paymentMethod.isDefault && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline"
                                                    onClick={() => setDefaultPaymentMethod(paymentMethod.id)}
                                                >
                                                    Definir como principal
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm px-2 text-error"
                                                onClick={() => deletePaymentMethod(paymentMethod.id)}
                                            >
                                                <Trash2 aria-hidden="true" />
                                                Desativar
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        if (activeSection === 'cobrancas') {
            return (
                <div className="card sf-dashboard-panel">
                    <div className="card-body">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <h3 className="text-lg font-semibold">Últimas cobranças</h3>
                                <p className="text-sm text-base-content/60">Eventos sincronizados pelo Asaas.</p>
                            </div>
                            <button type="button" className="btn btn-outline btn-sm" onClick={load}>
                                Atualizar
                            </button>
                        </div>

                        <div className="mt-4 overflow-x-auto">
                            <table className="table table-zebra table-sm">
                                <thead>
                                    <tr>
                                        <th>Vencimento</th>
                                        <th>Valor</th>
                                        <th>Status</th>
                                        <th>Forma</th>
                                        <th>Links</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payments.map((payment: any) => (
                                        <tr key={payment._id}>
                                            <td>{formatDate(payment.dueDate)}</td>
                                            <td>{formatCurrency(payment.amount)}</td>
                                            <td>
                                                <span className="badge badge-ghost">{payment.status || '-'}</span>
                                            </td>
                                            <td>{payment.billingType || '-'}</td>
                                            <td>
                                                <div className="flex flex-wrap gap-2">
                                                    {payment.transactionReceiptUrl && (
                                                        <a
                                                            className="link link-primary"
                                                            href={payment.transactionReceiptUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            Comprovante
                                                        </a>
                                                    )}
                                                    {payment.invoiceUrl && (
                                                        <a
                                                            className="link link-primary"
                                                            href={payment.invoiceUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            Cobrança
                                                        </a>
                                                    )}
                                                    {!payment.transactionReceiptUrl && !payment.invoiceUrl && '-'}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {payments.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="py-8 text-center text-base-content/60">
                                                Nenhuma cobrança sincronizada ainda.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="card sf-dashboard-panel">
                <div className="card-body gap-5">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <div className="rounded-2xl border border-base-200 bg-base-100/70 p-4">
                            <div className="text-xs uppercase text-base-content/55">Plano atual</div>
                            <div className="mt-2 font-semibold">
                                {currentPlan?.name || SALONFLOW_PLAN_LABEL[currentPlanKey]}
                            </div>
                            <div className="mt-1 text-sm text-base-content/65">
                                {currentPlanKey === 'trial'
                                    ? 'Teste gratuito'
                                    : currentPlanPrice !== null
                                      ? `${formatCurrency(currentPlanPrice)} / ${
                                            currentSubscription?.cycle === 'yearly' ? 'ano' : 'mês'
                                        }`
                                      : 'Valor não informado'}
                            </div>
                        </div>
                        <div className="rounded-2xl border border-base-200 bg-base-100/70 p-4">
                            <div className="text-xs uppercase text-base-content/55">Assinatura</div>
                            <div
                                className={`badge mt-2 ${
                                    cancellationScheduled ? 'badge-warning' : statusBadge(currentSubscription?.status)
                                }`}
                            >
                                {cancellationScheduled ? 'Cancelada' : statusLabel(currentSubscription?.status)}
                            </div>
                        </div>
                        <div className="rounded-2xl border border-base-200 bg-base-100/70 p-4">
                            <div className="text-xs uppercase text-base-content/55">Ciclo</div>
                            <div className="mt-2 font-semibold">{cycleLabel(currentSubscription?.cycle)}</div>
                        </div>
                        <div className="rounded-2xl border border-base-200 bg-base-100/70 p-4">
                            <div className="text-xs uppercase text-base-content/55">
                                {cancellationScheduled ? 'Acesso até' : 'Próxima cobrança'}
                            </div>
                            <div className="mt-2 font-semibold">
                                {formatDate(cancellationScheduled ? accessEndsAt : currentSubscription?.nextDueDate)}
                            </div>
                        </div>
                        <div className="rounded-2xl border border-base-200 bg-base-100/70 p-4">
                            <div className="text-xs uppercase text-base-content/55">Forma principal</div>
                            <div className="mt-2 flex items-center gap-3">
                                {defaultPaymentMethod && (
                                    <div className="flex h-8 w-[58px] shrink-0 items-center justify-center overflow-hidden rounded-md border border-base-200 bg-white">
                                        <PaymentBrandMark brand={defaultPaymentMethod.cardBrand} compact />
                                    </div>
                                )}
                                <div className="font-semibold">{paymentMethodSummary(defaultPaymentMethod)}</div>
                            </div>
                        </div>
                    </div>

                    {paymentMethods.length === 0 && (
                        <div className="alert alert-warning">
                            <div>
                                <div className="font-semibold">Nenhuma forma de pagamento cadastrada</div>
                                <div className="text-sm">
                                    Vá em <strong>Formas de pagamento</strong> para adicionar um cartão antes de contratar.
                                </div>
                            </div>
                            <button type="button" className="btn btn-sm" onClick={() => setActiveSection('pagamentos')}>
                                Adicionar agora
                            </button>
                        </div>
                    )}

                    {returnNotice && (
                        <div className={`alert ${returnNotice.className}`}>
                            <div>
                                <div className="font-semibold">Retorno do pagamento</div>
                                <div className="text-sm">{returnNotice.message}</div>
                            </div>
                            <div className="flex gap-2">
                                <button type="button" className="btn btn-sm" onClick={load}>
                                    Atualizar
                                </button>
                                <button type="button" className="btn btn-sm btn-ghost" onClick={clearReturnNotice}>
                                    Fechar
                                </button>
                            </div>
                        </div>
                    )}

                    {cancellationScheduled && (
                        <div className="alert alert-warning">
                            <div>
                                <div className="font-semibold">Assinatura cancelada</div>
                                <div className="text-sm">
                                    O acesso permanece ativo até {formatDate(accessEndsAt)}. Uma nova contratação cria outro ciclo.
                                </div>
                            </div>
                        </div>
                    )}

                    {!gateway.configured && (
                        <div className="alert alert-warning">
                            <div>
                                <div className="font-semibold">Asaas não configurado</div>
                                <div className="text-sm">
                                    Configure <code>ASAAS_API_KEY</code> antes de iniciar uma contratação real.
                                </div>
                            </div>
                        </div>
                    )}

                    {gateway.configured && !gateway.webhookConfigured && (
                        <div className="alert alert-warning">
                            <div>
                                <div className="font-semibold">Webhook Asaas pendente</div>
                                <div className="text-sm">
                                    Configure <code>ASAAS_WEBHOOK_TOKEN</code> no SalonFlow e no painel Asaas para confirmar pagamentos.
                                </div>
                            </div>
                        </div>
                    )}

                    {canCancelSubscription && (
                        <div className="rounded-2xl border border-base-200 bg-base-100/70 p-4 shadow-sm">
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                <div className="flex min-w-0 gap-3">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-success/10 text-success">
                                        <RefreshCw className="text-sm" aria-hidden="true" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <div className="font-semibold">Assinatura ativa</div>
                                            <span className="badge badge-success badge-sm">Renovação automática</span>
                                        </div>
                                        <div className="mt-1 text-sm text-base-content/65">
                                            Próxima cobrança em <strong>{formatDate(accessEndsAt)}</strong>. O acesso continua
                                            liberado enquanto os pagamentos forem confirmados pelo Asaas.
                                        </div>
                                    </div>
                                </div>
                                <details className="group rounded-xl border border-base-200 bg-base-200/35 px-3 py-2 lg:min-w-72">
                                    <summary className="cursor-pointer list-none text-sm font-medium text-base-content/70 outline-none transition group-open:text-base-content">
                                        <span className="inline-flex items-center gap-2">
                                            <Settings className="text-xs" aria-hidden="true" />
                                            Gerenciar renovação
                                        </span>
                                    </summary>
                                    <div className="mt-3 border-t border-base-300/60 pt-3">
                                        <p className="text-xs leading-relaxed text-base-content/60">
                                            Ao cancelar, nenhuma nova cobrança recorrente será feita. O plano segue vigente até{' '}
                                            {formatDate(accessEndsAt)}.
                                        </p>
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-error btn-xs mt-3"
                                            disabled={cancellingSubscription}
                                            onClick={() => setCancelRenewalDialogOpen(true)}
                                        >
                                            {cancellingSubscription ? 'Cancelando...' : 'Cancelar renovação'}
                                        </button>
                                    </div>
                                </details>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-6 pb-6">
            {renderSectionNav()}
            {renderSection()}

            {checkoutPlan && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-xl">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h3 className="text-lg font-semibold">Confirmar contratação</h3>
                                <p className="text-sm text-base-content/60">
                                    Revise o resumo antes de confirmar o plano.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                disabled={!!submittingPlan}
                                onClick={closeCheckoutConfirmation}
                            >
                                Fechar
                            </button>
                        </div>

                        <div className="mt-5 rounded-xl border border-base-200 bg-base-100/70 p-4">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-xs uppercase text-base-content/55">Plano</div>
                                    <div className="mt-1 text-lg font-semibold">{checkoutPlan.name}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-lg font-semibold text-primary">
                                        {formatCurrency(checkoutPlanPrice)}
                                    </div>
                                    <div className="text-xs text-base-content/60">
                                        /{cycle === 'yearly' ? 'ano' : 'mês'}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                                <div>
                                    <div className="text-xs text-base-content/55">Ciclo</div>
                                    <div className="font-medium">{cycle === 'yearly' ? 'Anual' : 'Mensal'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-base-content/55">Forma de pagamento</div>
                                    <div className="font-medium">{paymentMethodSummary(selectedPaymentMethod)}</div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 rounded-xl border border-base-200 bg-base-200/35 p-4 text-sm">
                            {checkoutIsSmallerPlan ? (
                                <p>
                                    Para contratar um plano menor, cancele primeiro a assinatura atual e depois faça a
                                    nova contratação.
                                </p>
                            ) : (
                                <p>
                                    A contratação inicia cobrança recorrente no cartão principal e a renovação acontece
                                    automaticamente a cada {cycle === 'yearly' ? 'ano' : 'mês'} até cancelamento.
                                </p>
                            )}
                        </div>

                        <label className="mt-4 flex cursor-pointer items-start gap-3 text-sm">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-primary checkbox-sm mt-0.5"
                                checked={checkoutTermsAccepted}
                                onChange={(event) => setCheckoutTermsAccepted(event.target.checked)}
                            />
                            <span>
                                Li e concordo com os{' '}
                                <a
                                    className="link link-primary"
                                    href="/termosDeUso"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Termos de Uso
                                </a>{' '}
                                e autorizo a contratação e a cobrança recorrente deste plano.
                            </span>
                        </label>

                        <div className="modal-action">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                disabled={!!submittingPlan}
                                onClick={closeCheckoutConfirmation}
                            >
                                Voltar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={!checkoutTermsAccepted || !!submittingPlan}
                                onClick={() => startCheckout(checkoutPlan)}
                            >
                                {submittingPlan === checkoutPlan.key ? 'Confirmando...' : 'Confirmar contratação'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {paymentMethodDialogOpen && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-3xl">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h3 className="text-lg font-semibold">Adicionar forma de pagamento</h3>
                                <p className="text-sm text-base-content/60">
                                    Os dados do cartão ficam apenas no envio para o Asaas. O SalonFlow salva só o token.
                                </p>
                            </div>
                            <button type="button" className="btn btn-ghost btn-sm" onClick={closePaymentMethodDialog}>
                                Fechar
                            </button>
                        </div>

                        <div className="mt-5 rounded-2xl border border-base-200 bg-base-100/60 p-4 space-y-4">
                            <div>
                                <div className="text-sm font-semibold">Titular e cobrança</div>
                            </div>

                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                <label className="form-control md:col-span-2">
                                    {requiredLabel('Nome/razão social')}
                                    <input
                                        className={inputClassName(paymentMethodFieldErrors.name)}
                                        autoComplete="organization"
                                        aria-invalid={paymentMethodFieldErrors.name}
                                        value={customer.name}
                                        onChange={(event) =>
                                            setCustomer((prev) => ({ ...prev, name: event.target.value }))
                                        }
                                    />
                                    {requiredFieldHint(paymentMethodFieldErrors.name)}
                                </label>
                                <label className="form-control">
                                    {requiredLabel('CPF/CNPJ')}
                                    <input
                                        className={inputClassName(paymentMethodFieldErrors.cpfCnpj)}
                                        inputMode="numeric"
                                        autoComplete="off"
                                        placeholder="000.000.000-00"
                                        aria-invalid={paymentMethodFieldErrors.cpfCnpj}
                                        value={formatCpfCnpj(customer.cpfCnpj)}
                                        onChange={(event) =>
                                            setCustomer((prev) => ({
                                                ...prev,
                                                cpfCnpj: formatCpfCnpj(event.target.value),
                                            }))
                                        }
                                    />
                                    {requiredFieldHint(paymentMethodFieldErrors.cpfCnpj)}
                                </label>
                                <label className="form-control">
                                    {requiredLabel('E-mail')}
                                    <input
                                        className={inputClassName(paymentMethodFieldErrors.email)}
                                        type="email"
                                        autoComplete="email"
                                        aria-invalid={paymentMethodFieldErrors.email}
                                        value={customer.email}
                                        onChange={(event) =>
                                            setCustomer((prev) => ({ ...prev, email: event.target.value }))
                                        }
                                    />
                                    {requiredFieldHint(paymentMethodFieldErrors.email)}
                                </label>
                                <label className="form-control">
                                    {requiredLabel('Telefone')}
                                    <input
                                        className={inputClassName(paymentMethodFieldErrors.mobilePhone)}
                                        inputMode="tel"
                                        autoComplete="tel"
                                        placeholder="(00) 00000-0000"
                                        aria-invalid={paymentMethodFieldErrors.mobilePhone}
                                        value={formatBrazilPhone(customer.mobilePhone)}
                                        onChange={(event) =>
                                            setCustomer((prev) => ({
                                                ...prev,
                                                mobilePhone: formatBrazilPhone(event.target.value),
                                            }))
                                        }
                                    />
                                    {requiredFieldHint(paymentMethodFieldErrors.mobilePhone)}
                                </label>
                                <label className="form-control">
                                    {requiredLabel('CEP')}
                                    <input
                                        className={inputClassName(paymentMethodFieldErrors.postalCode)}
                                        inputMode="numeric"
                                        autoComplete="postal-code"
                                        placeholder="00000-000"
                                        aria-invalid={paymentMethodFieldErrors.postalCode}
                                        value={formatCep(holderInfo.postalCode)}
                                        onChange={(event) =>
                                            setHolderInfo((prev) => ({
                                                ...prev,
                                                postalCode: formatCep(event.target.value),
                                            }))
                                        }
                                    />
                                    {requiredFieldHint(paymentMethodFieldErrors.postalCode)}
                                </label>
                                <label className="form-control">
                                    {requiredLabel('Número')}
                                    <input
                                        className={inputClassName(paymentMethodFieldErrors.addressNumber)}
                                        inputMode="numeric"
                                        autoComplete="address-line2"
                                        aria-invalid={paymentMethodFieldErrors.addressNumber}
                                        value={formatAddressNumber(holderInfo.addressNumber)}
                                        onChange={(event) =>
                                            setHolderInfo((prev) => ({
                                                ...prev,
                                                addressNumber: formatAddressNumber(event.target.value),
                                            }))
                                        }
                                    />
                                    {requiredFieldHint(paymentMethodFieldErrors.addressNumber)}
                                </label>
                            </div>

                        </div>

                        <div className="mt-4 rounded-2xl border border-base-200 bg-base-100/60 p-4 space-y-4">
                            <div>
                                <div className="text-sm font-semibold">Cartão de crédito</div>
                            </div>

                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                <label className="form-control md:col-span-2">
                                    {requiredLabel('Número do cartão')}
                                    <input
                                        className={inputClassName(paymentMethodFieldErrors.cardNumber)}
                                        inputMode="numeric"
                                        autoComplete="cc-number"
                                        placeholder="0000 0000 0000 0000"
                                        aria-invalid={paymentMethodFieldErrors.cardNumber}
                                        value={formatCardNumber(creditCard.number)}
                                        onChange={(event) =>
                                            setCreditCard((prev) => ({
                                                ...prev,
                                                number: formatCardNumber(event.target.value),
                                            }))
                                        }
                                    />
                                    {requiredFieldHint(paymentMethodFieldErrors.cardNumber)}
                                </label>
                                <label className="form-control">
                                    {requiredLabel('Mês de validade')}
                                    <input
                                        className={inputClassName(paymentMethodFieldErrors.expiryMonth)}
                                        inputMode="numeric"
                                        autoComplete="cc-exp-month"
                                        placeholder="MM"
                                        aria-invalid={paymentMethodFieldErrors.expiryMonth}
                                        value={formatCardExpiryMonth(creditCard.expiryMonth)}
                                        onChange={(event) =>
                                            setCreditCard((prev) => ({
                                                ...prev,
                                                expiryMonth: formatCardExpiryMonth(event.target.value),
                                            }))
                                        }
                                    />
                                    {requiredFieldHint(paymentMethodFieldErrors.expiryMonth)}
                                </label>
                                <label className="form-control">
                                    {requiredLabel('Ano de validade')}
                                    <input
                                        className={inputClassName(paymentMethodFieldErrors.expiryYear)}
                                        inputMode="numeric"
                                        autoComplete="cc-exp-year"
                                        placeholder="AAAA"
                                        aria-invalid={paymentMethodFieldErrors.expiryYear}
                                        value={formatCardExpiryYear(creditCard.expiryYear)}
                                        onChange={(event) =>
                                            setCreditCard((prev) => ({
                                                ...prev,
                                                expiryYear: formatCardExpiryYear(event.target.value),
                                            }))
                                        }
                                    />
                                    {requiredFieldHint(paymentMethodFieldErrors.expiryYear)}
                                </label>
                                <label className="form-control md:col-span-2">
                                    {requiredLabel('CVV')}
                                    <input
                                        className={inputClassName(paymentMethodFieldErrors.ccv)}
                                        type="password"
                                        inputMode="numeric"
                                        autoComplete="cc-csc"
                                        placeholder="000"
                                        aria-invalid={paymentMethodFieldErrors.ccv}
                                        value={formatSecurityCode(creditCard.ccv)}
                                        onChange={(event) =>
                                            setCreditCard((prev) => ({
                                                ...prev,
                                                ccv: formatSecurityCode(event.target.value),
                                            }))
                                        }
                                    />
                                    {requiredFieldHint(paymentMethodFieldErrors.ccv)}
                                </label>
                            </div>
                        </div>

                        <div className="modal-action">
                            <button type="button" className="btn btn-ghost" onClick={closePaymentMethodDialog}>
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={savingPaymentMethod}
                                onClick={savePaymentMethod}
                            >
                                {savingPaymentMethod ? 'Salvando...' : 'Salvar forma de pagamento'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {cancelRenewalDialogOpen && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-lg">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h3 className="text-lg font-semibold">Cancelar renovação automática</h3>
                                <p className="text-sm text-base-content/60">
                                    O plano continua ativo até {formatDate(accessEndsAt || currentSubscription?.currentPeriodEndAt)}.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                disabled={cancellingSubscription}
                                onClick={() => setCancelRenewalDialogOpen(false)}
                            >
                                Fechar
                            </button>
                        </div>

                        <div className="mt-5 rounded-2xl border border-warning/30 bg-warning/10 p-4">
                            <div className="flex gap-3">
                                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-warning/20 text-warning-content">
                                    <TriangleAlert className="text-sm" aria-hidden="true" />
                                </div>
                                <div className="space-y-2 text-sm text-base-content/75">
                                    <p>Nenhuma nova cobrança recorrente será feita no Asaas após esta confirmação.</p>
                                    <p>
                                        Seu acesso segue liberado até{' '}
                                        <strong>{formatDate(accessEndsAt || currentSubscription?.currentPeriodEndAt)}</strong>.
                                    </p>
                                    <p>Se quiser continuar com o plano sem interrupções, basta fechar este modal.</p>
                                </div>
                            </div>
                        </div>

                        <div className="modal-action">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                disabled={cancellingSubscription}
                                onClick={() => setCancelRenewalDialogOpen(false)}
                            >
                                Manter renovação
                            </button>
                            <button
                                type="button"
                                className="btn btn-error"
                                disabled={cancellingSubscription}
                                onClick={cancelSubscription}
                            >
                                {cancellingSubscription ? 'Cancelando...' : 'Confirmar cancelamento'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
